// src/context/DashboardDataContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useGlobalLoading } from './GlobalLoadingContext';
import { getProductSections as buildProductSections } from '../utils/selectorsProduct';
import { getJobSections as buildJobView } from '../utils/selectorsJobs';
import BACKEND_URL from '../config';
import { useAuth } from './AuthContext';
import { DataAndFilterContext } from './DataAndFilterContext'; // ⬅️ NEW: pull conversations from here

const DashboardDataContext = createContext(null);

export function DashboardDataProvider({ children }) {
  const { startLoading, stopLoading } =
    useGlobalLoading?.() || { startLoading: () => {}, stopLoading: () => {} };

  // 🔹 Conversations now come from DataAndFilterContext (single source of truth)
  const { filteredData: conversations } = useContext(DataAndFilterContext);

  const [jobMaps, setJobMaps] = useState(null);
  const [jtbdSatisfaction, setJtbdSatisfaction] = useState(null);
  const [jtbdNextGoals, setJtbdNextGoals] = useState(null);
  const [indices, setIndices] = useState({ jtbd_list: [], product_list: [] });
  const [error, setError] = useState(null);

  // Track if we've already fetched bootstrap for the current token
  const lastTokenRef = useRef(null);
  const hasFetchedRef = useRef(false);

  // Dataset options (+ current selection) for "Interview datasets" filter
  // datasetOptions comes from /api/dashboard/bootstrap -> payload.datasets
  // shape: [{ key, kind, label, industry_id?, product_id?, slug? }, ...]
  const [datasetOptions, setDatasetOptions] = useState([]);
  const [selectedDatasets, setSelectedDatasets] = useState([]); // array of keys

  const { token } = useAuth();

  const authHeader = useMemo(() => {
    if (!token) {
      console.warn(
        '[DashboardData] No access_token; endpoints that require JWT will fail (401).'
      );
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  useEffect(() => {
    let mounted = true;
    const aborter = new AbortController();

    async function robustJson(res) {
      try {
        return await res.json();
      } catch (e) {
        const txt = await res.text().catch(() => '');
        console.error('[DashboardData] Non-JSON response body:', txt);
        throw e;
      }
    }

    async function loadBootstrap() {
      // If we don’t have a token yet, don’t treat this as an error
      if (!token) {
        console.warn('[DashboardData] Skipping bootstrap: no JWT token yet.');
        return;
      }

      // Only skip if we already SUCCESSFULLY loaded bootstrap for this token
      if (hasFetchedRef.current && lastTokenRef.current === token) {
        console.log('[DashboardData] Bootstrap already loaded for this token; skipping.');
        return;
      }

      // IMPORTANT: do NOT mark "fetched" yet. We only mark it after success.

      setError(null);
      startLoading();
      try {
        const bootRes = await fetch(`${BACKEND_URL}/api/dashboard/bootstrap`, {
          headers: { ...authHeader },
          signal: aborter.signal,
        });

        let bootJson = {
          job_maps: {},
          jtbd_summaries: { satisfaction: {}, next_goals: {} },
          indices: { jtbd_list: [], product_list: [] },
        };
        if (!bootRes.ok) {
          const body = await bootRes.text().catch(() => '');
          console.error(
            `[DashboardData] /api/dashboard/bootstrap failed: ${bootRes.status} ${bootRes.statusText}`,
            body
          );
          setError(
            (prev) =>
              prev || new Error(`/api/dashboard/bootstrap ${bootRes.status}`)
          );
        } else {
          bootJson = await robustJson(bootRes);
          // Mark bootstrap as successfully loaded for this token
          hasFetchedRef.current = true;
          lastTokenRef.current = token;
        }

        if (!mounted) return;

        // 🔹 Job-map related data comes from bootstrap
        setJobMaps(bootJson.job_maps || {});
        setJtbdSatisfaction(
          (bootJson.jtbd_summaries &&
            bootJson.jtbd_summaries.satisfaction) ||
            {}
        );
        setJtbdNextGoals(
          (bootJson.jtbd_summaries &&
            bootJson.jtbd_summaries.next_goals) ||
            {}
        );
        setIndices(bootJson.indices || { jtbd_list: [], product_list: [] });

        const datasets = bootJson.datasets || [];
        setDatasetOptions(datasets);
        setSelectedDatasets([]);
      } catch (e) {
        // Allow retries if the request was aborted or failed
        hasFetchedRef.current = false;
        lastTokenRef.current = null;
        
        if (e.name === 'AbortError') {
          console.warn('[DashboardData] Aborted pending bootstrap fetch');
        } else {
          console.error(
            '[DashboardData] Unexpected bootstrap error:',
            e
          );
          if (mounted) {
            setJobMaps({});
            setJtbdSatisfaction({});
            setJtbdNextGoals({});
            setIndices({ jtbd_list: [], product_list: [] });
            setDatasetOptions([]);
            setSelectedDatasets([]);
            setError((prev) => prev || e);
          }
        }
      } finally {
        stopLoading();
      }
    }

    loadBootstrap();

    return () => {
      mounted = false;
      aborter.abort();
    };
    // This effect now effectively runs once per token
  }, [token, authHeader, startLoading, stopLoading]);

  const ready =
    jobMaps !== null &&
    jtbdSatisfaction !== null &&
    jtbdNextGoals !== null;

  // NEW: helper to filter customerStories by selected "Interview datasets"
  // - If no datasetOptions or no selectedDatasets, returns stories unchanged.
  // - Stories with no `origin` are treated as "self_upload" for backwards compatibility.
  const filterStoriesByDatasets = React.useCallback(
    (stories) => {
      if (!stories || !stories.length) return [];

      // If we don't have any dataset metadata yet, or nothing is selected,
      // treat as "no dataset filter" and return all stories.
      if (
        !datasetOptions.length ||
        !selectedDatasets ||
        selectedDatasets.length === 0
      ) {
        return stories;
      }

      const selectedSet = new Set(selectedDatasets);

      const selectedIndustryIds = new Set(
        datasetOptions
          .filter((d) => d.kind === 'industry' && selectedSet.has(d.key))
          .map((d) => d.industry_id)
      );
      const selectedProductIds = new Set(
        datasetOptions
          .filter((d) => d.kind === 'product' && selectedSet.has(d.key))
          .map((d) => d.product_id)
      );
      const includeSelfUpload = selectedSet.has('self_upload');

      return stories.filter((story) => {
        // 👇 default missing origin to "self_upload"
        const origin = story.origin || 'self_upload';

        if (origin === 'self_upload') {
          return includeSelfUpload;
        }

        if (origin === 'ai_interview') {
          const inds = new Set(story.industry_ids || []);
          const prods = new Set(story.product_ids || []);

          const matchesIndustry = [...inds].some((id) =>
            selectedIndustryIds.has(id)
          );
          const matchesProduct = [...prods].some((id) =>
            selectedProductIds.has(id)
          );

          return matchesIndustry || matchesProduct;
        }

        // Unknown origin -> keep it (so we don't accidentally hide data)
        return true;
      });
    },
    [datasetOptions, selectedDatasets]
  );

  const getProductSections = React.useCallback(
    (productKey) => {
      if (!ready) return { sections: [] };
      const stories = filterStoriesByDatasets(conversations || []);
      return buildProductSections({
        productKey,
        customerStories: stories,
        jobMaps,
      });
    },
    [ready, conversations, jobMaps, filterStoriesByDatasets]
  );

  const getJobSections = React.useCallback(
    (jobName) => {
      if (!ready) return { sections: [] };
      const stories = filterStoriesByDatasets(conversations || []);
      return buildJobView({
        jobName,
        customerStories: stories,
        jobMaps,
        jtbdSatisfaction,
        jtbdNextGoals,
      });
    },
    [
      ready,
      conversations,
      jobMaps,
      jtbdSatisfaction,
      jtbdNextGoals,
      filterStoriesByDatasets,
    ]
  );

  const value = React.useMemo(
    () => ({
      ready,
      error,
      // 🔹 Expose customerStories derived from the shared conversations
      customerStories: conversations || [],
      jobMaps,
      jtbdSatisfaction,
      jtbdNextGoals,
      indices,
      // dataset filter state for the Dashboard header
      datasetOptions,
      selectedDatasets,
      setSelectedDatasets,
      getProductSections,
      getJobSections,
    }),
    [
      ready,
      error,
      conversations,
      jobMaps,
      jtbdSatisfaction,
      jtbdNextGoals,
      indices,
      datasetOptions,
      selectedDatasets,
      getProductSections,
      getJobSections,
    ]
  );

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
}

export function useDashboardData() {
  const ctx = useContext(DashboardDataContext);
  if (!ctx)
    throw new Error('useDashboardData must be used within <DashboardDataProvider>');
  return ctx;
}
